import { Controller } from "@hotwired/stimulus";

// The admin email browser: open an email out of the catalogue table and read it
// in the modal. Searching and sorting the table is DataTables' job, and the
// audience tabs are plain links — this controller only drives the modal.
// Width is a CSS swap on the frame; brand and colour scheme are rendered
// server-side, so those two fetch the frame again.
export default class extends Controller {
  static targets = [
    "frame", "spinner", "stage", "title", "subtitle",
    "brandField", "brandSelect", "houseBrand", "noBrand", "openLink",
    "widthButton", "themeButton",
  ];
  static values = { frameUrl: String };

  connect() {
    this.dark = false;
    this.mailer = null;
    this.variant = null;
  }

  open(event) {
    const { mailer, variant, name, subtitle, brandable, feature } = event.params;
    this.mailer = mailer;
    this.variant = variant;
    this.titleTarget.textContent = name;
    this.subtitleTarget.textContent = subtitle;

    // Every email renders one restaurant's data, house-brand mail included;
    // only the chrome differs, so the picker is offered either way.
    const offered = this.offerBrands(feature);
    // Only a patron email wears a restaurant's brand; offering the picker on
    // the rest would suggest a choice that isn't there. A brandable email with
    // nobody to render it on says so rather than showing an empty select.
    this.brandFieldTarget.classList.toggle("d-none", offered === 0);
    this.houseBrandTarget.classList.toggle("d-none", brandable);
    this.noBrandTarget.classList.toggle("d-none", offered > 0);
    this.render();
  }

  // Narrow the picker to clients that have the feature this email is about —
  // a reservation email on a restaurant that takes none renders an empty
  // shell. Returns how many are left, and keeps the selection on one of them.
  offerBrands(feature) {
    let selected = null;
    const options = Array.from(this.brandSelectTarget.options);

    options.forEach((option) => {
      const has =
        feature === "any" ||
        option.dataset.features.split(" ").includes(feature);
      option.hidden = !has;
      option.disabled = !has;
      if (has && !selected) selected = option;
    });

    const current = this.brandSelectTarget.selectedOptions[0];
    if (selected && (!current || current.disabled)) {
      this.brandSelectTarget.value = selected.value;
    }
    return options.filter((option) => !option.disabled).length;
  }

  reload() {
    this.render();
  }

  setWidth(event) {
    this.activate(event.currentTarget, this.widthButtonTargets);
    this.frameTarget.style.width = `${event.params.width}px`;
  }

  setTheme(event) {
    this.activate(event.currentTarget, this.themeButtonTargets);
    this.dark = event.params.dark;
    this.render();
  }

  render() {
    if (!this.mailer) return;

    const url = new URL(this.frameUrlValue, window.location.origin);
    url.searchParams.set("mailer", this.mailer);
    url.searchParams.set("variant", this.variant);
    url.searchParams.set("restaurant_id", this.brandSelectTarget.value);
    if (this.dark) url.searchParams.set("dark", "1");
    const href = url.toString();

    this.openLinkTarget.href = href;
    this.stageTarget.classList.toggle("is-dark", this.dark);

    // Asking for what is already showing fires no `load`, so the spinner would
    // sit over a rendered email forever.
    if (this.frameTarget.src === href) {
      this.spinnerTarget.classList.add("d-none");
      return;
    }
    this.spinnerTarget.classList.remove("d-none");
    this.frameTarget.src = href;
  }

  loaded() {
    if (this.frameTarget.getAttribute("src")) this.spinnerTarget.classList.add("d-none");
  }

  clear() {
    this.frameTarget.removeAttribute("src");
    this.spinnerTarget.classList.remove("d-none");
  }

  // Buttons in a group are exclusive: the one just pressed is the active one.
  activate(button, group) {
    group.forEach((element) => {
      element.classList.toggle("active", element === button);
    });
  }
}
